import { Controller } from "@hotwired/stimulus";

const COUNTRIES = [
  {
    code: "VN",
    name: "Việt Nam",
    dial: "+84",
    flag: "🇻🇳",
    format: "### ### ####",
    digits: 10,
  },
  {
    code: "US",
    name: "United States",
    dial: "+1",
    flag: "🇺🇸",
    format: "(###) ###-####",
    digits: 10,
  },
  {
    code: "JP",
    name: "Japan",
    dial: "+81",
    flag: "🇯🇵",
    format: "###-####-####",
    digits: 11,
  },
  {
    code: "SG",
    name: "Singapore",
    dial: "+65",
    flag: "🇸🇬",
    format: "#### ####",
    digits: 8,
  },
  {
    code: "TH",
    name: "Thailand",
    dial: "+66",
    flag: "🇹🇭",
    format: "###-###-####",
    digits: 10,
  },
  {
    code: "KR",
    name: "South Korea",
    dial: "+82",
    flag: "🇰🇷",
    format: "###-####-####",
    digits: 11,
  },
];

function formatPhone(digits, pattern) {
  pattern = pattern.replaceAll('#', '_')
  let result = "",
  di = 0;
  for (let i = 0; i < pattern.length; i++) {
    result += pattern[i] === "_" ? di < digits.length ? digits[di++] : pattern[i] : pattern[i];
  }
  if(result === "") {
    return pattern
  }
  return result;
}

const SEP = /[\s\-\(\)]/

function slotCount(format) {
  return format.split("").filter(c => !SEP.test(c)).length
}

// Connects to data-controller="phone-numpad"
export default class extends Controller {
  static targets = [
    "input",
    "popup",
    "maskDisplay",
    "progress",
    "countryBtn",
    "countryDropdown",
    "confirmBtn"
  ];

  connect() {
  }

  disconnect() {
    document.removeEventListener("mousedown", this._onOutsideClick);
  }

  countryBtnTargetConnected(element) {
    this._country = this.getCountry()[0];
    this._digits = "";

    this._renderCountryBtn();
    this._updateDisplay()
  }

  countryDropdownTargetConnected(element) {
    this._renderDropdown();
    this._onOutsideClick = (e) => {
      if (!this.element.contains(e.target)) {
        this.hidePopup();
        this.hideDropdown();
      }
    };
    document.addEventListener("mousedown", this._onOutsideClick);
    this._onKeydown = this._handleKeydown.bind(this)
  }

  maskDisplayTargetConnected(element) {
  }

  openPopup() {
    this.popupTarget.classList.remove("d-none");
    this.hideDropdown();
    this._updateDisplay();
    this._positionPopup(this.popupTarget)
    document.addEventListener("keydown", this._onKeydown)
  }

  hidePopup() {
    this.popupTarget.classList.add("d-none");
    document.removeEventListener("keydown", this._onKeydown)
  }

  // Handler
  _handleKeydown(e) {
    // Bỏ qua nếu user đang gõ trong search box
    if (e.target === this.countrySearchTarget) return

    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault()
      this.pressKeyDirect(e.key)
      return
    }

    switch (e.key) {
      case "Backspace":
        e.preventDefault()
        this.backspace()
        break
      case "Delete":
        e.preventDefault()
        this.clear()
        break
      case "Enter":
        e.preventDefault()
        this.confirm()
        break
      case "Escape":
        e.preventDefault()
        this._hidePopup()
        break
    }
  }

  // pressKey dùng trực tiếp key value thay vì từ dataset
  pressKeyDirect(key) {
    if (this._digits.length >= slotCount(this._country.format)) return
    this._digits += key
    this._sync()
    this._highlightKey(key)  // visual feedback
  }

  _sync() {
    this._updateDisplay();
  }

  // Highlight button tương ứng trên numpad khi gõ bàn phím
  _highlightKey(key) {
    const btn = this.popupTarget.querySelector(`[data-key="${key}"]`)
    if (!btn) return
    btn.classList.add("active")
    setTimeout(() => btn.classList.remove("active"), 100)
  }

  toggleDropdown(e) {
    e.stopPropagation();
    this.countryDropdownTarget.classList.toggle("d-none");
    if (!this.countryDropdownTarget.classList.contains("d-none")) {
      this._positionPopup(this.countryDropdownTarget)
      this.countrySearchTarget?.focus()
    }
    this.hidePopup();
  }

  _positionPopup(popupEl) {
    const inputRect  = this.element.getBoundingClientRect()
    const popupHeight = popupEl.offsetHeight  // height thực sau khi d-none removed

    // Nếu popup chưa render (vừa remove d-none) thì estimate bằng scrollHeight
    const estimatedHeight = popupEl.scrollHeight || 320

    const spaceBelow = window.innerHeight - inputRect.bottom
    const spaceAbove = inputRect.top

    const openAbove = spaceBelow < estimatedHeight && spaceAbove > spaceBelow

    if (openAbove) {
      popupEl.style.top    = "auto"
      popupEl.style.bottom = `calc(100% + 8px)`
    } else {
      popupEl.style.top    = "calc(100% + 8px)"
      popupEl.style.bottom = "auto"
    }
  }

  hideDropdown() {
    this.countryDropdownTarget.classList.add("d-none");
  }

  // ── Key actions ──────────────────────────────────────────────

  pressKey(e) {
    this.pressKeyDirect(e.currentTarget.dataset.key)
  }

  backspace() {
    this._digits = this._digits.slice(0, -1);
    this._updateDisplay();
  }

  clear() {
    this._digits = "";
    this._updateDisplay();
  }

  confirm() {
    if (this._digits.length !== this._country.digits) {
      this._shake();
      return;
    }
    const value = `${this._country.dial} ${formatPhone(this._digits, this._country.format)}`;
    this.inputTarget.value = value;
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.hidePopup();
  }

  selectCountry(e) {
    const code = e.currentTarget.dataset.code;
    this._country = this.getCountry().find((c) => c.code === code);
    this._digits = "";
    this._renderCountryBtn();
    this.hideDropdown();
    this.openPopup();
    this._updateDisplay()
  }

  // ── Private ──────────────────────────────────────────────────

  _updateDisplay() {
    const formatted = formatPhone(this._digits, this._country.format);
    const isValid = this._digits.length === this._country.digits;
    const remaining = this._country.digits - this._digits.length;
    this.maskDisplayTarget.textContent = formatted || "";
    this.maskDisplayTarget.placeholder = "Enter phone number...";

    // Progress bar
    const pct = (this._digits.length / this._country.digits) * 100;
    this.progressTarget.style.width = `${pct}%`;
    this.progressTarget.className = `progress-bar ${isValid ? "bg-success" : "bg-primary"}`;

    // Confirm button
    if (isValid) {
      this.confirmBtnTarget.textContent = "✓ Confirm";
      this.confirmBtnTarget.className = "btn btn-success flex-fill fw-bold";
    } else {
      this.confirmBtnTarget.textContent = `Remaining: ${remaining}/${this._country.digits}`;
      this.confirmBtnTarget.className = "btn btn-outline-primary flex-fill";
    }
  }

  _renderCountryBtn() {
    const c = this._country;
    this.countryBtnTarget.innerHTML = `<span class="me-1" style="font-size:1.1em">${c.flag}</span>
       <span class="fw-semibold">${c.dial}</span>
       <small class="ms-1 text-muted">▼</small>`;
  }

  _renderDropdown() {
    this.countryDropdownTarget.innerHTML = this.getCountry().map(
      (c) => `
      <li>
        <button type="button" class="dropdown-item d-flex align-items-center gap-2 p-2"
                data-action="click->phone-numpad#selectCountry"
                data-code="${c.code}">
          <span style="font-size:1.2em">${c.flag}</span>
          <span class="flex-fill">${c.name}</span>
          <small class="text-primary fw-bold">${c.dial}</small>
        </button>
      </li>
    `,
    ).join("");
  }

  _shake() {
    this.maskdisplayTarget.classList.add("text-danger");
    this.element
      .querySelector("[data-phone-numpad-target='display']")
      .animate(
        [
          { transform: "translateX(-6px)" },
          { transform: "translateX(6px)" },
          { transform: "translateX(-6px)" },
          { transform: "translateX(0)" },
        ],
        { duration: 400, easing: "ease" },
      );
    setTimeout(() => this.maskdisplayTarget.classList.remove("text-danger"), 500);
  }

  getCountry() {
    return COUNTRIES.filter((c) => c.code == 'US')
  }
}
